import React, { PropsWithChildren, useMemo } from "react";
import { DisabledAndLoadingContext, useDisabledAndLoading } from "../../../core/context/DisabledAndLoading";
import { useZendeskAuthorization } from "../../ZendeskRedirect";
import { useParams } from "react-router";
import { addNotification } from "../../../AppNotification";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import { OrganizationSelectList } from "../../UserOrganizations";
import { LoginContext } from "./Context";
import Typography from "@mui/material/Typography";
import { LoginReply } from "../../../core/api/contracts/login";
import { useLoginStateProvider } from "../../../LoginStateProvider";
import { useLoginEventTrackers } from "./useLoginEventTrackers";
import { isClassicOrg, Organization } from "../../../core/api/contracts/Organization";

export interface OrganizationSelectProps {
  organizations: Organization[];
  onSelect: (org: Organization) => void;
}
type MultiOrgView = React.ElementType<OrganizationSelectProps>;

export function LoginProvider(props: PropsWithChildren<{ multiOrgView?: MultiOrgView }>) {
  const [zendeskRedirect] = useZendeskAuthorization();
  const { jwt } = useParams<{ jwt: string }>();
  const disabledAndLoading = useDisabledAndLoading();
  const loginStateProvider = useLoginStateProvider();
  const tracker = useLoginEventTrackers();

  const multiOrgs = useMemo(
    () => {
      const orgs = loginStateProvider.status?.organizations?.filter(isClassicOrg);
      return orgs?.length > 1 ? orgs: null;
    },
    [loginStateProvider.status]
  );

  const onSuccess = (loginReply: LoginReply) => {
    tracker.trackLogin();
    if (jwt === "zendesk") {
      zendeskRedirect().catch((err) => {
        if (typeof err === "string") {
          addNotification({ level: "warning", message: err });
        }
      });
    } else if (loginReply) {
      disabledAndLoading.setLoading(true);
      loginStateProvider.handleLoginReply(loginReply).finally(() => {
        disabledAndLoading.setLoading(false);
      });
    }
  };

  if (multiOrgs) {
    return props.multiOrgView ? (
      React.createElement(props.multiOrgView, { organizations: multiOrgs, onSelect: loginStateProvider.setLoginOrg })
    ) : (
      <Container maxWidth="sm">
        <Box p={1}>
          <Box>
            <Typography variant="h5">Select Organization</Typography>
          </Box>
          <OrganizationSelectList
            organizations={multiOrgs}
            gotoDefault={true}
            onSelect={loginStateProvider.setLoginOrg}
          />
        </Box>
      </Container>
    );
  }

  return (
    <DisabledAndLoadingContext.Provider value={disabledAndLoading}>
      <LoginContext.Provider value={{ onLoginSuccess: onSuccess }}>{props.children}</LoginContext.Provider>
    </DisabledAndLoadingContext.Provider>
  );
}

export default LoginProvider;
